import APIV5 from '~/models/api';
import Utils from '~/scripts/services/utils';
import { THRESHOLD_NB_CELLS_INSIDE_CELL } from '~/components/ktable/ktable.controller';
import templateModalUploadClassic from '~/components/upload/upload-classic/modal/modal-upload-classic.html';
import ModalUploadClassicController from '~/components/upload/upload-classic/modal/modal-upload-classic.controller';
import rowDetailsModal from '~/components/ktable/modals/row-details/row-details-modal.component';
import { WFAffectModal } from '../worflow-affect/workflow-affect';
import { createWidgetQuery } from '../dashboard.model';
import WorkflowAffect from '~/scripts/services/model/workflow-affect';

/* globals Dashboard,Ktable */

const subrequestBase = '/widget/ktable/subrequest';


const isCell = (/** @type {any} */ x) => 'getRow' in x;

/**
 * @type {Dashboard.Widget.WidgetBuilderFn<Dashboard.KTableWidgetOptions>}
 */
export async function createKTableWidget(widgetKID, extras) {
	/** @type {import('@knitiv/api-client-javascript').QueryBuilder<'widget' | 'mainQuery' | 'queryRequest'>} */
	const builder = APIV5.instance.createQueryBuilder();

	const kid = extras.queryResolve?.kid ?? 'K_NODE;KSYSQUERY';
	const linker = extras.queryResolve?.linker ?? '0';

	// fetch widget and queries
	builder
		.select(selectBuilder => selectBuilder
			.addColumn(columnBuilder => columnBuilder
				.as('widget')
				.withTarget('K_NODE;WIDGET'),
			)
			.addColumn(columnBuilder => columnBuilder
				.as('mainQuery')
				.withTarget(kid, linker)
				.withPath(pathBuilder => pathBuilder
					.flowDown(kid, linker),
				),
			)
			.addColumn(columnBuilder => columnBuilder
				.as('queryRequest')
				.withTarget(kid, linker)
				.withFormat('KATTR:QUERY')
				.withPath(pathBuilder => pathBuilder
					.flowDown(kid, linker),
				),
			),
		)
		.from(fromBuilder => fromBuilder
			.addElement('K_NODE;WIDGET'),
		)
		.where(whereBuilder => whereBuilder
			.addRule(ruleBuilder => ruleBuilder
				.equalKid('K_NODE;WIDGET', widgetKID),
			),
		);

	/** @type {import('@knitiv/api-client-javascript').QueryBuilder<'action' | 'type' | 'request'>} */
	const actions = APIV5.instance.createQueryBuilder();

	// fetch actions
	actions
		.select(selectBuilder => selectBuilder
			.addColumn(columnBuilder => columnBuilder
				.as('action')
				.withTarget('K_NODE;WIDGETACTION'),
			)
			.addColumn(columnBuilder => columnBuilder
				.as('type')
				.withFormat('KID')
				.withTarget('K_NODE;WDGTACTTYP')
				.withPath(pathBuilder => pathBuilder
					.flowDown('K_NODE;WDGTACTTYP'),
				),
			)
			.addColumn(columnBuilder => columnBuilder
				.as('request')
				.withFormat('KID')
				.withTarget('K_NODE;KSYSQUERY')
				.withPath(pathBuilder => pathBuilder
					.flowDown('K_NODE;WIDGET')
					.flowDown('K_NODE;KSYSQUERY'),
				),
			),
		)
		.from(fromBuilder => fromBuilder
			.addElement('K_NODE;WIDGETACTION'),
		)
		.where(whereBuilder => whereBuilder
			.addRule(ruleBuilder => ruleBuilder
				.equalKid('K_NODE;WIDGET', widgetKID)
				.withPath(p => p
					.flowUp('K_NODE;WIDGET'),
				),
			),
		);

	const { rows } = (await builder.fetchAll(
		undefined,
		undefined,
		{ id: 'create-ktable-widget.rows' },
		// eslint-disable-next-line unicorn/no-await-expression-member
	)).resolved();

	// Should always have 1 child since kid is unique
	const queryResult = rows[0];

	/** @type {import('@knitiv/api-client-javascript').RowsWithColumns<"action" | "type" | "request">} */
	let extraActions = [];
	try {
		extraActions = (await actions.fetchAll(
			undefined,
			undefined,
			{ id: 'create-ktable-widget.extra-actions' },
			// eslint-disable-next-line unicorn/no-await-expression-member
		)).resolved().rows;
	}
	catch (error) {
		console.error(error);
	}

	/** @type {Ktable.Options} */
	const tableOptions = {
		height: 8,
		actions: {
			export: true,
			refresh: true,
		},
		tableOptions: {
			layout: 'fitColumns',
		},
		display: {
			status: true,
		},
	};

	/**
	 * @type {any[]}
	 */
	const subqueries = [];

	if (extraActions.length > 0) {
		const promises = [];
		for (const [, action] of extraActions.entries()) {
			promises.push(async () => {
				const currentAction = action.action[0];
				const type = action.type[0];
				const request = action.request[0];

				if (type.kid === 'K_NODE;WDGTACTTYP_SUBQUERY') {
					subqueries.push({
						name: currentAction.name,
						kid: request.kid,
					});
				}

				if (!queryResult.widget) {
					return;
				}

				// eslint-disable-next-line no-use-before-define
				return createKtableWidgetActionType(
					type.kid,
					queryResult.widget[0],
					currentAction,
					extras,
				);
			});
		}

		if (tableOptions.actions) {
			tableOptions.actions.extra = await Promise.all(promises.map(x => x()));
		}

		// eslint-disable-next-line
		// tableOptions.tableOptions.rowClickMenu = (row, event) => {
		// 	// if (!extras.$location.path().includes(subrequestBase)) {
		// 	// 	return false;
		// 	// }
		// 	if (tableOptions.actions.extra.length > 1) {
		// 		return tableOptions.actions.extra;
		// 	}

		// 	return false;
		// };

		if (tableOptions.tableOptions) {
			tableOptions.tableOptions.cellClick = (event, cell) => {
				const data = cell.getValue();
				const row = cell.getRow();

				if (
					tableOptions.actions?.extra?.length === 1
					&& data.cells.length <= THRESHOLD_NB_CELLS_INSIDE_CELL
				) {
					const action = tableOptions.actions.extra[0]; // 0 is separator
					action.action(event, row);
				}
			};
		}
	}

	const mainQuery = queryResult.mainQuery[0];
	const queryRequest = queryResult.queryRequest[0];

	const widgetQuery = createWidgetQuery(queryRequest.name, subqueries);

	const settings = await APIV5.instance.settings.get({
		id: 'settings',
		node_kid: widgetKID,
	}, { id: 'create-ktable-widget.settings' });

	// TODO: fetch options
	const options = settings ? {
		kid: settings.kid,
		data: Utils.JSONParseSafe(settings.kjson),
	} : null;

	/** @type {Dashboard.KTableWidgetOptions} */
	const widgetScope = {
		options,
		tableOptions,
		name: queryResult.widget[0].name,
		query: extras.query ?? widgetQuery,
		queryNode: mainQuery,
		variables: extras.variables ?? {},
		rows: [],
		reference: null,
		kid: widgetKID,
		type: 'ktable',
		onBeforeColumnCreate: column => column,
		// if (select.type === 'workflow') {
		// 	console.log('select', select);
		// 	console.log('column', column);
		// 	console.log('query', query);
		// 	console.log('query', query);
		// 	console.log('options.actions.extra', extraActions);
		// 	console.log('----------------------');

		// 	for (const action of extraActions) {
		// 		// If it contains a WF action
		// 		if (action.type[0].kid === "K_NODE;WDGTACTTYP_WF") {
		// 		// column.headerContextMenu = [
		// 		// 	{

		// 		// 	}
		// 		// ];
		// 		}
		// 	}
		// }
	};

	return widgetScope;
}

/**
 * @type {Dashboard.Widget.WidgetBuilderFn<Dashboard.PieChartWidgetOptions>}
 */
export async function createPieChartWidget(widgetKID) {
	/** @type {import('@knitiv/api-client-javascript').QueryBuilder<'widget' | 'query'>} */
	const builder = APIV5.instance.createQueryBuilder();

	builder
		.select(selectBuilder => selectBuilder
			.addColumn(columnBuilder => columnBuilder
				.as('widget')
				.withTarget('K_NODE;WIDGET'),
			)
			.addColumn(columnBuilder => columnBuilder
				.as('query')
				.withTarget('K_NODE;KSYSQUERY')
				.withFormat('KATTR:QUERY')
				.withPath(pathBuilder => pathBuilder
					.flowDown('K_NODE;KSYSQUERY'),
				),
			),
		)
		.from(fromBuilder => fromBuilder
			.addElement('K_NODE;WIDGET'),
		)
		.where(whereBuilder => whereBuilder
			.addRule(ruleBuilder => ruleBuilder
				.equalKid('K_NODE;WIDGET', widgetKID),
			),
		);

	// eslint-disable-next-line
	const { rows } = (await builder.fetchAll()).resolved();

	const widget = {
		node: rows[0].widget[0],
		query: JSON.parse(rows[0].query[0].name),
	};

	const queryResult = await APIV5.instance.queryExec({
		query: widget.query,
	}, { id: 'create-pie-chart-widget.query' });

	const data = queryResult;

	const settings = await APIV5.instance.settings.get({
		id: 'settings',
		node_kid: widgetKID,
	}, { id: 'create-pie-chart-widget.settings' });

	const options = settings ? {
		kid: settings.kid,
		data: Utils.JSONParseSafe(settings.kjson),
	} : null;

	/** @type {Dashboard.PieChartWidgetOptions} */
	const widgetScope = {
		data: data.result.rows,
		kid: widgetKID,
		query: widget.query,
		name: widget.node.name,
		options,
		type: 'pie-chart',
	};

	return widgetScope;
}

/**
 * @type {Dashboard.Widget.WidgetBuilderFn<Dashboard.PieChartWidgetOptions>}
 */
export async function createProgressWidget(widgetKID, extras) {
	/** @type {import('@knitiv/api-client-javascript').QueryBuilder<'widget' | 'query-1' | 'query-2' | 'mode'>} */
	const builder = APIV5.instance.createQueryBuilder();

	builder
		.select(selectBuilder => selectBuilder
			.addColumn(columnBuilder => columnBuilder
				.as('widget')
				.withTarget('K_NODE;WIDGET'),
			)
			.addColumn(columnBuilder => columnBuilder
				.as('query-1')
				.withTarget('K_NODE;KSYSQUERY')
				// .withFormat('KATTR:QUERY')
				.withPath(pathBuilder => pathBuilder
					.flowDown('K_NODE;KSYSQUERY'),
				),
			)
			.addColumn(columnBuilder => columnBuilder
				.as('query-2')
				.withTarget('K_NODE;KSYSQUERY', 'K_NODE;AVANCEMENT')
				// .withFormat('KATTR:QUERY')
				.withPath(pathBuilder => pathBuilder
					.flowDown('K_NODE;KSYSQUERY', 'K_NODE;AVANCEMENT'),
				),
			)
			.addColumn(columnBuilder => columnBuilder
				.as('mode')
				.withTarget('K_NODE;WDGTMODE_PROGRESS')
				.withPath(pathBuilder => pathBuilder
					.flowDown('K_NODE;WDGTMODE_PROGRESS'),
				),
			),
		)
		.from(fromBuilder => fromBuilder
			.addElement('K_NODE;WIDGET'),
		)
		.where(whereBuilder => whereBuilder
			.addRule(ruleBuilder => ruleBuilder
				.equalKid('K_NODE;WIDGET', widgetKID),
			),
		);

	// eslint-disable-next-line
	const { rows } = (await builder.fetchAll()).resolved();

	const widget = {
		node: rows[0].widget[0],
		queryBase: rows[0]['query-1'][0].kid,
		queryCompare: rows[0]['query-2'][0].kid,
		mode: rows[0].mode[0].kid,
	};

	const queryResultBase = await APIV5.instance.queryRun({
		query_kid: widget.queryBase,
		onlytotal: true,
	}, { id: 'create-progress-widget.query-base' });

	const queryResultCompare = await APIV5.instance.queryRun({
		query_kid: widget.queryCompare,
		onlytotal: true,
	}, { id: 'create-progress-widget.query-compare' });

	const base = queryResultBase.result.total;
	const compare = queryResultCompare.result.total;

	const data = widget.mode === 'K_NODE;WDGTMODE_PROGRESS_01' // Réalisé, 02 = Plan d'action
		? compare / base
		: (base - compare) / base;
	const percent = Math.floor(data * 100);

	const settings = await APIV5.instance.settings.get({
		id: 'settings',
		node_kid: widgetKID,
	}, { id: 'create-progress-widget.settings' });

	const options = settings ? {
		kid: settings.kid,
		data: Utils.JSONParseSafe(settings.kjson),
	} : null;

	let ktable = {};
	if (extras.fromDashboard === true) {
		ktable = await createKTableWidget(widgetKID, {
			...extras,
			// override ktable query fetching to get avancement instead of the defautl query
			queryResolve: {
				linker: 'K_NODE;AVANCEMENT',
			},
		});
	}

	/** @type {Dashboard.PieChartWidgetOptions} */
	const widgetScope = {
		data,
		percent,

		kid: widgetKID,
		queryBase: widget.queryBase,
		queryCompare: widget.queryCompare,
		name: widget.node.name,
		options,
		type: 'progress',

		// ktable backward compatible
		...ktable,
	};

	return widgetScope;
}

/**
 * @type {Dashboard.Widget.WidgetBuilderFn<Dashboard.GaugeChartWidgetOptions>}
 */
export async function createGaugeChartWidget(widgetKID) {
	/** @type {import('@knitiv/api-client-javascript').QueryBuilder<'widget' | 'query'>} */
	const builder = APIV5.instance.createQueryBuilder();

	builder
		.select(selectBuilder => selectBuilder
			.addColumn(columnBuilder => columnBuilder
				.as('widget')
				.withTarget('K_NODE;WIDGET'),
			)
			.addColumn(columnBuilder => columnBuilder
				.as('query')
				.withTarget('K_NODE;KSYSQUERY')
				.withFormat('KATTR:QUERY')
				.withPath(pathBuilder => pathBuilder
					.flowDown('K_NODE;KSYSQUERY'),
				),
			),
		)
		.from(fromBuilder => fromBuilder
			.addElement('K_NODE;WIDGET'),
		)
		.where(whereBuilder => whereBuilder
			.addRule(ruleBuilder => ruleBuilder
				.equalKid('K_NODE;WIDGET', widgetKID),
			),
		);

	// eslint-disable-next-line
	const { rows } = (await builder.fetchAll()).resolved();

	const widget = {
		node: rows[0].widget[0],
		query: JSON.parse(rows[0].query[0].name),
	};

	const queryResult = await APIV5.instance.queryExec({
		query: widget.query,
	}, { id: 'create-gauge-chart-widget.query' });

	const data = queryResult;

	const settings = await APIV5.instance.settings.get({
		id: 'settings',
		node_kid: widgetKID,
	}, { id: 'create-gauge-chart-widget.settings' });

	// console.log('settings', settings);

	// TODO: fetch options
	const options = settings ? {
		kid: settings.kid,
		data: Utils.JSONParseSafe(settings.kjson),
	} : null;

	/** @type {Dashboard.GaugeChartWidgetOptions} */
	const widgetScope = {
		data: data.result.rows,
		total: data.result.total,
		kid: widgetKID,
		query: widget.query,
		name: widget.node.name,
		options,
		type: 'gauge-chart',
	};

	return widgetScope;
}

/**
 * @param widget
 * @param action
 * @param root0
 * @param root0.$timeout
 * @param root0.$location
 * @param root0.$uibModal
 * @param root0.$injector
 */
export async function createDocumentImportAction(
	widget,
	action,
	{ $injector }) {
	const $uibModal = $injector.get('$uibModal');
	const Notification = $injector.get('Notification');

	return {
		label: cell => {
			const selectedRows = cell.getTable().getSelectedRows();

			let message = `<i class="fa fa fa-file"></i> ${action.name}`;
			if (selectedRows.length > 0) {
				message += ` (${selectedRows.length} ligne${selectedRows.length > 1 ? 's' : ''})`;
			}
			return message;
		},
		action: async (_, cellOrRow) => {
			const actionObject = await APIV5.instance.objectInfo({ kid: action.kid }, { id: 'widget.action' });
			let relation;
			/**
			 * @type {import('@knitiv/api-client-javascript').LinkDefinition | undefined}
			 */
			let linkClass;

			/**
			 * @type {import('@knitiv/api-client-javascript').LinkDefinition | undefined}
			 */
			let linkLinker;

			const composedOf = actionObject?.link?.list?.from['K_NODE;COMPOSED_OF'];
			if (composedOf) {
				const links = Object.values(composedOf.list);
				relation = links.find(link => link.class_kid === 'K_NODE;DOCUMENT');
				linkClass = links.find(link => link.class_kid === 'K_NODE;ANYTHING' && link.linker_kid === 'K_NODE;SYS_CLASSE');
				linkLinker = links.find(link => link.class_kid === 'K_NODE;ANYTHING' && link.linker_kid === 'K_NODE;LINK');
			}

			if (!linkClass) {
				const message = 'Widget non configuré : classe manquante !';
				Notification.error({
					title: 'Erreur',
					message,
				});
				throw new Error(message);
			}

			if (!relation) {
				const message = 'Impossible de déterminer le type de document à importer : relation non trouvée';
				Notification.error({
					title: 'Erreur',
					message,
				});
				throw new Error(message);
			}

			const fileTypeKid = relation.objnum2_kid;

			const selectedRows = cellOrRow.getTable().getSelectedRows();

			const rows = selectedRows.length > 0 ? selectedRows : [isCell(cellOrRow) ? cellOrRow.getRow() : cellOrRow];

			// vvv Le type de fichier prédéterminé
			// eslint-disable-next-line no-console
			console.log('Type de fichier', fileTypeKid);

			const objectsTolink = rows.map(r => {
				const data = r.getData();
				const row = data.root;
				row.class_kid = linkClass?.objnum2_kid;
				row.linker_kid = linkLinker?.objnum2_kid || '0';
				return row;
			});

			// vvv Les noeuds sur lesquels ratacher le document choisi
			// eslint-disable-next-line no-console
			console.log('Objects to link', rows, objectsTolink);

			const options = {
				maxFiles:              1,
				isa:                   fileTypeKid,
				links:                 objectsTolink,
				hideContextsFromLinks: true,
			};

			const modal = $uibModal.open({
				ariaLabelledBy:  'modal-upload-classic-title',
				ariaDescribedBy: 'modal-upload-classic-body',
				template:        templateModalUploadClassic,
				controller:      ModalUploadClassicController,
				controllerAs:    '$ctrl',
				size:            'full',
				resolve:         {
					options:   () => options,
				},
				windowClass:     'modal-upload-classic',
			});

			const answer = await modal.result;

			if (answer?.type === 'success') {
				// eslint-disable-next-line no-console
				console.log('answer', answer);

				await cellOrRow.ktable.refresh();
			}
		},
	};
}

/**
 * @param widget
 * @param action
 * @param root0
 * @param root0.$timeout
 * @param root0.$location
 * @param root0.$uibModal
 * @param root0.$injector
 */
export async function createEditAction(
	widget,
	action,
	{ $injector }) {
	const $uibModal = $injector.get('$uibModal');

	return {
		label: `<i class="fa fa-pencil"></i> ${action.name}`,
		action: async (_, cellOrRow) => {
			const selectedRows = cellOrRow.getTable().getSelectedRows();

			if (selectedRows.length > 0) {
				throw new Error('Too many rows selected');
			}

			const row = (isCell(cellOrRow) ? cellOrRow.getRow() : cellOrRow).getData();

			const { kid } = row.root;

			const modal = $uibModal.open(rowDetailsModal({
				resolve: {
					kid: () => kid,
					readonly: () => false,
					locked: () => false,
					// editMode: () => 'edit',
				},
			}));

			await modal.result;

			await cellOrRow.ktable.refresh();
		},
	};
}

/**
 * @type {Dashboard.Widget.WidgetActionBuilderFn}
 */
export async function createSubrequestAction(widget, action, { $timeout, $location, $rootScope }) {
	return {
		label: `<i class="fa fa-terminal"></i> ${action.name}`,
		action: async (_, cell) => {
			$timeout(async () => {
				/** @type {Ktable.RowData} */
				// @ts-ignore
				const data = cell.getData();

				if ($location.path().includes(subrequestBase)) {
					$rootScope.$broadcast('widget.subrequest', {
						base: subrequestBase,
						widget: widget.kid,
						action: action.kid,
						root: data.root.kid,
					});
					// $location.path(`${subrequestBase}/${widget.kid}/${action.kid}`);
					// $location.search('selected', data.root.kid);
				}
				else {
					$rootScope.$broadcast('widget.subrequest', {
						base: subrequestBase,
						widget: widget.kid,
						action: action.kid,
						root: data.root.kid,
					});
					// window.open(`${subrequestBase}/${widget.kid}/${action.kid}?selected=${data.root.kid}`);
				}
			});
		},
	};
}

/**
 * @type {Dashboard.Widget.WidgetActionBuilderFn}
 */
export async function createRowDetailsAction(widget, action, { $timeout }) {
	return {
		label: `<i class="fa fa-eye"></i> ${action.name}`,
		action: async (_, cell) => {
			$timeout(async () => {
				/** @type {Ktable.RowData} */
				// @ts-ignore
				const data = cell.getData();

				window.open(`/form/${data.root.kid}`);
			});
		},
	};
}

/**
 * @type {Dashboard.Widget.WidgetActionBuilderFn}
 */
export async function createWorkflowAction(
	widget,
	action,
	{ $timeout, $injector, dashboardKid },
) {
	const Notification = $injector.get('Notification');
	const $location = $injector.get('$location');
	const $translate = $injector.get('$translate');

	return {
		label: cell => {
			const selectedRows = cell.getTable().getSelectedRows();

			let message = `<i class="fa fa fa-arrows-h"></i> ${action.name}`;
			if (selectedRows.length > 0) {
				message += ` (${selectedRows.length} ligne${selectedRows.length > 1 ? 's' : ''})`;
			}
			return message;
		},
		action: async (_, cellOrRow) => {
			const selectedRows = cellOrRow.getTable().getSelectedRows();

			let rows = [];

			rows = selectedRows.length > 0 ? selectedRows : [isCell(cellOrRow) ? cellOrRow.getRow() : cellOrRow];

			let conditions = {
				workflow: '',
				status: '',
			};

			/** @type {any[]} */
			const errors = [];

			/** @type {any[]} */
			const workflowsToModify = [];

			// eslint-disable-next-line unicorn/no-array-for-each
			rows.forEach(async (selectedRow, index) => {
				/** @type {Ktable.RowData} */
				const data = selectedRow.getData();

				const hasWorkflowColumn = Object.entries(data).find(([key, col]) => {
					if (!key.startsWith('f')) {
						return false;
					}

					/** @type {Ktable.CellData} */
					const currentColumn = col;
					if (currentColumn.column?.type === 'workflow') {
						return true;
					}

					return false;
				});

				if (hasWorkflowColumn) {
					/** @type {[string, Ktable.CellData]} */
					const [, col] = hasWorkflowColumn;
					if (col.cells.length > 1) {
						errors.push({
							message: `Ligne ${index}: Plusieurs workflows`,
							workflow: col.cells,
						});
					}
					else {
						const wfCell = col.cells[0];
						if (wfCell.wf && wfCell.wf.length > 0) {
							if (wfCell.wf.length === 1) {
								const workflow = wfCell.wf[0];
								if (conditions.workflow === '') {
									conditions = {
										workflow: workflow.wf_kid,
										status: workflow.status_kid,
									};
								}
								let isErroring = false;
								if (conditions.workflow !== workflow.wf_kid) {
									errors.push({
										message: 'Toutes les lignes doivent être liées au même workflow',
										workflow,
									});
									isErroring = true;
								}
								if (conditions.status !== workflow.status_kid) {
									errors.push({
										message: 'Tous les workflows doivent avoir le même statut',
										workflow,
									});
									isErroring = true;
								}
								if (!workflow.affected) {
									errors.push({
										message: 'Vous n\'êtes pas affecté à ce workflow',
										workflow,
									});
									isErroring = true;
								}

								if (!isErroring) {
									workflowsToModify.push(workflow);
								}
							}
							else {
								errors.push({
									message: `Ligne ${index}: Plusieurs workflows`,
									workflow: col,
								});
							}
						}
					}
				}
			});

			if (errors.length > 0) {
				// afficher liste des erreurs puis
				// surligner lignes en erreur
				console.error(errors);
				for (const error of errors) {
					Notification.error({
						title: 'Impossible d\'effectuer la mise a jour',
						message: error.message,
					});
				}
			}
			else if (workflowsToModify.length === 0) {
				Notification.error({
					title: 'Impossible d\'effectuer la mise a jour',
					message: 'Aucun workflow à modifier',
				});
			}
			else if (workflowsToModify.length === 1) {
				// if only ONE workflow is selected, go directly to workflow
				$timeout(async () => {
					const workflowAffectKid = workflowsToModify[0].kid;
					const parameters = {
						widget: widget.kid,
					};
					$location
						.path(`/workflow/${workflowAffectKid}`)
						.search({
							from: `/dashboard/${dashboardKid}`,
							params: JSON.stringify(parameters),
						});
				});
			}
			else {
				// if MORE than one workflow is selected, open modal
				$timeout(async () => {
					const modal = new WFAffectModal($injector);

					const workflowAffectKid = workflowsToModify[0].kid;

					const wfAffect = await APIV5.instance.wfAffectInfo({
						wfaffect_kid: workflowAffectKid,
					}, { id: 'create-workflow-action.action' });

					const affect = new WorkflowAffect($injector, wfAffect);

					const answer = await modal.open({
						onClick: async (/** @type {any} */ controller) => {
							const actionKid = controller.currentAction.kid;

							const wfallocator = [];
							if (Array.isArray(controller.allocator) && !controller.affectAuto) {
								wfallocator.push(...controller.allocator.map((/** @type {any} */ allocator) => allocator.kid));
							}

							const wfnotify = [];
							if (Array.isArray(controller.notify)) {
								wfnotify.push(...controller.notify.map((/** @type {any} */ notify) => notify.kid));
							}

							/** @type {any[]} */
							const orders = [];

							for (const workflow of workflowsToModify) {
								switch (actionKid) {
								case 'abort': {
									orders.push(
										APIV5.instance.createWFAffectAbort({
											wfaffect_kid: workflow.kid,
											wfcomment: controller.comment,
										}),
									);

									break;
								}

								case 'reassign': {
									orders.push(
										APIV5.instance.createWFAffectReassign({
											wfaffect_kid: workflow.kid,
											wfallocator,
										}),
									);

									break;
								}

								default: {
									orders.push(
										APIV5.instance.createWFAffectSetAction({
											wfaffect_kid: workflow.kid,
											wfaction_kid: actionKid,
											wfstate_kid: controller.state,
											wfallocator,
											wfnotify,
											wfcomment: controller.comment,
										}),
									);

									break;
								}
								}
							}

							const list = await APIV5.instance.multiOrderRequest(orders, { id: 'create-workflow-action.list' });

							// @ts-ignore
							const responseOrders = list.orders;

							for (const responseOrder of responseOrders) {
								if (responseOrder.error) {
									Notification.error({
										title:     'Le workflow changement n\'a pas abouti',
										message:   responseOrder.error.errno_desc,
									});
								}
								else {
									Notification.success({
										title:     $translate.instant('object.successfully-action-title'),
										message:   'Les changements ont bien été apportés au workflow',
									});
								}
							}

							modal.close({ controller, type: 'success' });
						},
						onCancel: (/** @type {any} */ controller) => {
							modal.close({ controller, type: 'cancel' });
						},
						affect,
						options: {
							// defaultAction: action,
							modal:         true,
							hideHistory:   true,
						},
						actionKid: wfAffect.wfaction.kid,
						affectKid: workflowAffectKid,
					});

					if (answer.type === 'success') {
						const row = (isCell(cellOrRow) ? cellOrRow.getRow() : cellOrRow).getData();

						await row.ktable.refresh();
					}
				});
			}
		},
	};
}

/**
 * @type {Dashboard.Widget.WidgetActionBuilderFn}
 */
export async function createDefaultAction(widget, action) {
	return {
		label: `<i class="fa fa-cogs"></i> ${action.name}`,
		disabled: true,
	};
}

/**
 * @type {Dashboard.Widget.CreateWidgetActionFn}
 */
export async function createKtableWidgetActionType(
	actionType,
	widget,
	action,
	extra,
) {
	/** @type {Dashboard.Widget.WidgetActionBuilderObj} */
	const actions = {
		'K_NODE;WDGTACTTYP_SUBQUERY': createSubrequestAction,
		'K_NODE;WDGTACTTYP_ROW_DETAILS': createRowDetailsAction,
		'K_NODE;WDGTACTTYP_WF': createWorkflowAction,
		'K_NODE;WDGTACTTYP_DOCIMPORT': createDocumentImportAction,
		'K_NODE;WDGTACTTYP_EDIT': createEditAction,
		default: createDefaultAction,
	};

	const widgetActionBuilder = actionType in actions ? actions[actionType] : actions.default;

	return widgetActionBuilder(widget, action, extra);
}

/**
 * @type {Dashboard.Widget.CreateWidgetFn}
 */
export async function createWidget(widgetType, widgetKid, extra) {
	/** @type {Dashboard.Widget.WidgetBuilderObj} */
	const widgets = {
		'K_NODE;WDGT_KTABLE': createKTableWidget,
		'K_NODE;WDGT_PIE_CHART': createPieChartWidget,
		'K_NODE;WDGT_GAUGE_CHART': createGaugeChartWidget,
		'K_NODE;WDGT_PROGRESS': createProgressWidget,
		// @ts-ignore
		default: () => {
			console.error(`Invalid widget type "${widgetType}"`);
		},
	};

	const widgetBuilder = widgetType in widgets ? widgets[widgetType] : widgets.default;

	return widgetBuilder(widgetKid, extra);
}
